import React from 'react';

/**
 * GlowingReLogo — branding mark used across the app.
 *
 * Renders the Remoddash "RE" image inside a white rounded-square
 * container with a colored glow halo. Canonical visual spec lives
 * inline at pages/LandingPage.js:95-101 (blue) and
 * pages/PublicRemodify.js:530-537 (purple) — this component mirrors
 * those specs exactly so every surface looks identical.
 *
 * Usage notes:
 *   • `variant="blue"` (default) → provider/Remoddash side.
 *   • `variant="purple"`         → customer-facing /remodify flow.
 *   • The Gateway page intentionally keeps the spelled-out wordmark
 *     (`RemoddashLogo type="full"`) and does NOT use this component.
 *   • On a white background the white container disappears — the glow
 *     + RE mark become the brand accent. Intentional.
 *
 * Props:
 *   size       (number, default 36) outer container side length in px.
 *   variant    ("blue"|"purple", default "blue")
 *   className  (string) extra utility classes on the outer container.
 *   testId     (string, default "glowing-re-logo") data-testid.
 */
const GLOW = {
  blue:   '0 0 28px 4px rgba(96, 165, 250, 0.75)',
  purple: '0 0 28px 4px rgba(168, 85, 247, 0.55)',
};
const IMG = {
  blue:   '/remodify-logo-blue.png',
  purple: '/remodify-logo-purple.png',
};

const GlowingReLogo = ({
  size = 36,
  variant = 'blue',
  className = '',
  testId = 'glowing-re-logo',
}) => {
  // Image sizing matches canonical headers (LandingPage.js:128-132 blue,
  // PublicRemodify.js:487-492 purple): image height = ~64% of container
  // (h-7 on w-11 / h-9 on w-14), width: auto, object-contain. The image
  // is ~2.14:1 horizontally so its natural width exceeds the container —
  // this is intentional and matches canonical exactly (overflow is NOT
  // clipped; the "Re" extends softly past the rounded edges).
  const imgH = Math.round(size * 0.64);
  return (
    <div
      className={`rounded-2xl bg-white flex items-center justify-center ${className}`}
      style={{
        width: size,
        height: size,
        boxShadow: GLOW[variant] || GLOW.blue,
      }}
      data-testid={testId}
    >
      <img
        src={IMG[variant] || IMG.blue}
        alt="RE"
        className="object-contain"
        style={{ height: imgH, width: 'auto' }}
      />
    </div>
  );
};

export default GlowingReLogo;
